import { z } from 'zod';
import { CargoTotalsSchema } from './registry.js';
import { CargoClientPaymentStatusSchema, CargoCarrierPaymentStatusSchema } from './enums.js';
import { CargoNonnegMoneySchema } from './article.js';

// Cargo registry STATISTICS WIRE shapes (#1886). The `GET /cargo/statistics`
// aggregates run over the SAME filtered selection + row visibility as
// `GET /cargo` / `GET /cargo/summary` / `GET /cargo/charts`. Quantity sums come
// from the dirty free-text weight/volume columns via `parseDirtyNumber`, so the
// sub-bucket `weight`/`volume` are plain JS numbers (unparseable cells skipped),
// matching `cargo-charts.ts`. `totals` reuses `CargoTotalsSchema` verbatim.

/**
 * @aiPurpose counts-only mirror of the summary indicators for the Statistics
 *   mode (#1888). Each bucket carries ONLY a count (+ a completeness percentage
 *   where meaningful) — NO `articles` arrays, NO PII, NO row identifiers.
 * @aiSecurity unlike `CargoIndicatorsSchema`, this shape deliberately drops the
 *   per-row `article` strings (FR-10/BR-5): the statistics surface must never
 *   leak the underlying article values, so the producer never selects or emits
 *   them.
 */
export const CargoStatisticsIndicatorsSchema = z.object({
  paidNotIssued: z.object({ count: z.number().int().nonnegative() }),
  missingResponsibleManager: z.object({
    count: z.number().int().nonnegative(),
    fillPercent: z.number(),
  }),
  missingPathFormat: z.object({
    count: z.number().int().nonnegative(),
    fillPercent: z.number(),
  }),
  noInsurance: z.object({ count: z.number().int().nonnegative() }),
  // #1939: counts-only — NEVER articles (FR-10/BR-5), matching the other
  // non-fill buckets.
  rejectedDocument: z.object({ count: z.number().int().nonnegative() }),
});
export type CargoStatisticsIndicators = z.infer<typeof CargoStatisticsIndicatorsSchema>;

/**
 * @aiPurpose `GET /cargo/statistics` envelope (#1886/#2164): counts-only
 *   aggregates over the FULL filtered selection — totals, indicators, lifecycle
 *   stage counts, payment-status breakdowns, per-manager / per-carrier / monthly
 *   distributions, per-pathFormat route breakdown (`routes`), average transit
 *   days (`avgDaysInTransit`), and prior-period comparison (`comparison`), all
 *   over the same filtered selection the list endpoint paginates.
 * @aiInvariant the #2164 fields (`monthly[].chinaAmount`, `routes`,
 *   `avgDaysInTransit`, `comparison`) are optional-additive: the producer always
 *   populates them, but they are optional on the wire so pre-#2165
 *   consumers/fixtures that omit them still validate (backward-compatible
 *   rollout).
 * @aiSecurity contains NO `article` strings, NO PII, NO file keys, NO source
 *   ids, NO raw rows — counts and sums only. Every bucket is computed WITHIN the
 *   caller's visibility scope, so returning it to a manager exposes nothing
 *   beyond that manager's own rows.
 */
export const CargoStatisticsResponseSchema = z.object({
  totals: CargoTotalsSchema,
  indicators: CargoStatisticsIndicatorsSchema,
  lifecycle: z.object({
    total: z.number().int().nonnegative(),
    inTransit: z.number().int().nonnegative(),
    arrivedNotIssued: z.number().int().nonnegative(),
    issued: z.number().int().nonnegative(),
    withoutManager: z.number().int().nonnegative(),
  }),
  payments: z.object({
    client: z.array(
      z.object({
        status: CargoClientPaymentStatusSchema,
        count: z.number().int().nonnegative(),
      }),
    ),
    carrier: z.array(
      z.object({
        status: CargoCarrierPaymentStatusSchema,
        count: z.number().int().nonnegative(),
      }),
    ),
  }),
  managers: z.array(
    z.object({
      managerId: z.string().uuid().nullable(),
      managerName: z.string().nullable(),
      count: z.number().int().nonnegative(),
      weight: z.number(),
      volume: z.number(),
      inTransit: z.number().int().nonnegative(),
      arrivedNotIssued: z.number().int().nonnegative(),
      issued: z.number().int().nonnegative(),
    }),
  ),
  carriers: z.array(
    z.object({
      carrier: z.string(),
      count: z.number().int().nonnegative(),
      weight: z.number(),
      volume: z.number(),
    }),
  ),
  monthly: z.array(
    z.object({
      month: z.string(),
      count: z.number().int().nonnegative(),
      weight: z.number(),
      volume: z.number(),
      issuedCount: z.number().int().nonnegative(),
      chinaAmount: CargoNonnegMoneySchema.optional(),
    }),
  ),
  routes: z
    .array(
      z.object({
        pathFormat: z.string().nullable(),
        count: z.number().int().nonnegative(),
        weight: z.number(),
        volume: z.number(),
      }),
    )
    .optional(),
  avgDaysInTransit: z.number().nullable().optional(),
  comparison: z
    .object({
      total: z.number().int().nonnegative(),
      inTransit: z.number().int().nonnegative(),
      arrivedNotIssued: z.number().int().nonnegative(),
      issued: z.number().int().nonnegative(),
      withoutManager: z.number().int().nonnegative(),
      totalWeightKg: z.number(),
      totalVolumeM3: z.number(),
      totalChinaAmount: CargoNonnegMoneySchema,
    })
    .nullable()
    .optional(),
});
export type CargoStatisticsResponse = z.infer<typeof CargoStatisticsResponseSchema>;
